import React from 'react';
import classNames from 'classnames';
import { useTPAComponentsConfig } from '../TPAComponentsProvider';
import styles from './Toast.module.scss';
/**
 * Mirrors `ToastSkin` from `wix-ui-tpa/cssVars`. Only the three members the store
 * actually produces are modelled (`ToastEffect['skin']` in comments-types.ts).
 */
export const ToastSkin = {
    success: 'success',
    error: 'error',
    status: 'status',
};
/** Mirrors `ToastPlacement`. Only the two members used by toast-root.tsx are modelled. */
export const ToastPlacement = {
    inline: 'inline',
    bottomFullWidth: 'bottomFullWidth',
};
/**
 * Drop-in replacement for `Toast` from `wix-ui-tpa/cssVars`.
 *
 * No `@wix/site-ui` equivalent yet (spec'd at zeroheight § 07 Notifications).
 *
 * Behaviour note: the WUT original does not own auto-dismiss either — toast-root.tsx
 * drives the 5s timer and unmounts. This keeps that contract, so the caller is
 * unchanged. `role="alert"` is used for error/success and `status` for the quiet skin
 * so screen readers announce the loud ones assertively.
 */
export const Toast = ({ children, className, 
/* No defaults for `skin` / `placement`: `Toast.defaultProps` sets only `shouldShowCloseButton`
   and `isShown`, so an unskinned toast writes NO skin state and paints no background. Defaulting
   to `status` painted #212121 where upstream is transparent — which the broken class lookup had
   been masking. Every call site in this app passes both. */
skin, placement, shouldShowCloseButton = false, onClose, 'data-hook': dataHook, }) => {
    /*
     * `mobile` / `rtl` from TPAComponentsContext, as upstream (`Toast.js` wraps its root in a
     * TPAComponentsConsumer and writes both as states). This port emitted neither, which left
     * `.root.mobile .message` and `.root.rtl .closeButtonWrapper` unmatched: a mobile toast — which
     * is how toast-root.tsx renders it on mobile — kept the desktop message margins and a 20px gap
     * it should not have, and in RTL the close button stayed pinned to the wrong side.
     */
    const { mobile = false, rtl = false } = useTPAComponentsConfig();
    return (React.createElement("div", { className: classNames(styles.root, 
        /* `skin-${skin}` / `placement-${placement}`: the sheet's classes are prefixed, so the bare
           `styles[skin]` / `styles[placement]` lookups resolved to undefined — a toast rendered
           with white text and no background, and never became the fixed full-width bottom banner
           on mobile. */
        skin ? styles[`skin-${skin}`] : undefined, placement ? styles[`placement-${placement}`] : undefined, { [styles.mobile]: mobile, [styles.rtl]: rtl }, 
        /* `isShown` unconditionally: WUT's defaultProps set it to true and nothing here passes it,
         but the class is not optional — the hide/translate rules live under `:not(:isShown)`, so
         without it a toast renders hidden and 27px off-position. */
        styles.isShown, className), role: skin === ToastSkin.status ? 'status' : 'alert', "data-skin": skin, "data-hook": dataHook },
        React.createElement("span", { role: "presentation", className: styles.gapBeforeMessage }),
        React.createElement("span", { "data-hook": "message", className: styles.message }, children),
        !shouldShowCloseButton && React.createElement("span", { role: "presentation", className: styles.gapAfterMessage }),
        shouldShowCloseButton && (React.createElement("div", { className: styles.closeButtonWrapper },
            React.createElement("button", { type: "button", "data-part": "closeButton", "data-hook": "closeButton", className: styles.closeButton, onClick: onClose, "aria-label": "Close" },
                React.createElement("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "currentColor", "aria-hidden": "true", focusable: "false" },
                    React.createElement("path", { d: "M19.2928932,3.99989322 L20,4.707 L12.7068932,11.9998932 L20,19.2928932 L19.2928932,20 L11.9998932,12.7068932 L4.707,20 L3.99989322,19.2928932 L11.2928932,11.9998932 L3.99989322,4.707 L4.707,3.99989322 L11.9998932,11.2928932 L19.2928932,3.99989322 Z", fillRule: "evenodd" })))))));
};
//# sourceMappingURL=Toast.js.map